import {
  Component,
  OnInit,
  ViewChild,
  Input,
  OnDestroy,
  Output,
  EventEmitter,
} from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { IContent } from '../../core/models/i-content';
import { UntypedFormControl } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { CmsPaginationService } from 'src/app/shared/services/cms-pagination.service';
import { AppConfig } from 'src/app/app-config';
import { NotificationBarComponent } from 'src/app/shared/notification-bar/notification-bar.component';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { CmsDialogComponent } from '../../shared/components/cms-dialog/cms-dialog.component';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { ContentListService } from '../shared/services/cmsapi/content-list.service';
import { ContentListStateService } from '../shared/services/cmsapi/content-list-state.service';
import { WithProcessing } from 'src/app/shared/mixins/with-processing';
import { ContentType } from 'src/app/content-management/shared/enums/content-type';
import { Features } from 'src/app/core/feature-flags/features';
import { FeatureFlagsService } from 'src/app/core/feature-flags/feature-flag.service';
import { ContentStatus } from 'src/app/shared/enums/content-status';
import { ContentTypeRouterPaths } from 'src/app/shared/enums/content-type-router-paths';
import { ContentTypes } from 'src/app/shared/enums/content-types';

class ViewContentListComponentBase {
  constructor() {}
}

const ViewContentListComponentMixinBase = WithProcessing(
  ViewContentListComponentBase
);
@Component({
  selector: 'app-view-content-list',
  templateUrl: './view-content-list.component.html',
})
export class ViewContentListComponent
  extends ViewContentListComponentMixinBase
  implements OnInit, OnDestroy
{
  @Input() contentListCode: string;
  @Output() triggerUpdateAddContentListData = new EventEmitter<IContent[]>();
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  private pageSize = 10;

  contentDataSource: MatTableDataSource<IContent> = new MatTableDataSource();
  totalContentCount: number;
  contentResultLimit: number;
  displayedColumns: string[] = ['title', 'pinPosition'];
  pagingOptions: number[] = [10, 25, 50, 100];
  contentResultIndex = 1;
  pageSizeFilter = new UntypedFormControl(this.pageSize);
  pageSubscriptions: Subscription[] = [];
  paginatedPinnedContent: IContent[] = [];
  paginatedUnPinnedContent: IContent[] = [];
  pinnedContent: IContent[] = [];
  unPinnedContent: IContent[] = [];
  contentRemoved: IContent[] = [];
  contentAdded: IContent[] = [];
  removeAllPinnedContent: boolean;

  constructor(
    private contentListService: ContentListService,
    public cmsPaginationService: CmsPaginationService,
    private appConfig: AppConfig,
    public notificationBar: MatSnackBar,
    private contentListStateService: ContentListStateService,
    private dialog: MatDialog,
    private dialogService: DialogService,
    private featureFlagsService: FeatureFlagsService
  ) {
    super();
    this.contentResultLimit = this.appConfig.contentResultLimit;
  }

  ngOnInit(): void {
    this.paginator.pageSize = this.pageSize;
    const pageSizeFilterSubscription =
      this.pageSizeFilter.valueChanges.subscribe((value) => {
        this.cmsPaginationService.selectPageSize(this.contentDataSource, value);
      });

    this.loadContent();
    this.pageSubscriptions.push(pageSizeFilterSubscription);
  }

  loadContent(pageSize?: number): void {
    const tableSubscription = this.process$(
      this.contentListService.getContent$(this.contentListCode, pageSize)
    ).subscribe(
      (content) => {
        this.totalContentCount = content.totalCount;
        this.pinnedContent = content.articles.filter((con) => con.pinPosition);
        this.unPinnedContent = content.articles.filter(
          (con) => !con.pinPosition
        );
        this.paginateData(this.paginator);
        this.contentAdded.length = 0;
        this.contentRemoved.length = 0;
      },
      (error) => {
        this.dialogService.openErrorDialog({
          message:
            'An error has occurred whilst loading the content. Please contact IT.',
        });
      }
    );
    this.pageSubscriptions.push(tableSubscription);
  }

  paginateData($event): void {
    this.contentDataSource.data = [
      ...this.pinnedContent,
      ...this.unPinnedContent,
    ];
    this.contentDataSource.paginator = this.paginator;
    let paginatedContent = this.contentDataSource.data.slice(
      $event.pageIndex * $event.pageSize,
      $event.pageIndex * $event.pageSize + $event.pageSize
    );
    if (!paginatedContent.length) {
      paginatedContent = this.contentDataSource.data.slice(
        ($event.pageIndex - 1) * $event.pageSize,
        ($event.pageIndex - 1) * $event.pageSize + $event.pageSize
      );
    }
    this.paginatedPinnedContent = paginatedContent.filter(
      (con) => con.pinPosition
    );
    this.paginatedUnPinnedContent = paginatedContent.filter(
      (content) => !content.pinPosition
    );
    this.cmsPaginationService.populatePageButtonArray(this.contentDataSource);
  }

  goToNextPage(contentDataSource: MatTableDataSource<IContent>): void {
    const shouldLoadNewContent = this.shouldTriggerNextLoadIndex(
      contentDataSource,
      contentDataSource.paginator.pageIndex + 2
    );

    if (contentDataSource.paginator.hasNextPage() && !shouldLoadNewContent) {
      this.cmsPaginationService.nextPage(contentDataSource);
    } else if (this.contentListStateService.isContentListChanged()) {
      this.displaySaveCurrentChangesNotificationBar();
    } else {
      this.loadNextContentResultIndex();
      this.cmsPaginationService.nextPage(contentDataSource);
    }
  }

  selectPage(contentDataSource: MatTableDataSource<IContent>, num): void {
    const shouldLoadNewContent = this.shouldTriggerNextLoadIndex(
      contentDataSource,
      num
    );

    if (!shouldLoadNewContent) {
      this.cmsPaginationService.selectPage(contentDataSource, num);
    } else if (this.contentListStateService.isContentListChanged()) {
      this.displaySaveCurrentChangesNotificationBar();
    } else {
      this.loadNextContentResultIndex();
      this.cmsPaginationService.selectPage(contentDataSource, num);
    }
  }

  isEnabledForContentType$(contentType: ContentType): Observable<boolean> {
    return this.featureFlagsService.isEnabledForContentType$(contentType);
  }

  getFeatureForContentType(contentType: ContentType) {
    return this.featureFlagsService.getFeatureForContentType(contentType);
  }

  getRouteForContentType(
    contentType: string,
    contentId: string,
    state: ContentStatus
  ) {
    return this.featureFlagsService.getRouteForContentType(
      contentType,
      contentId,
      state
    );
  }

  private shouldTriggerNextLoadIndex(
    contentDataSource: MatTableDataSource<IContent>,
    pageNumber
  ): boolean {
    const largestVisibleIndex =
      pageNumber * contentDataSource.paginator.pageSize;
    const loadNewContent =
      largestVisibleIndex > this.contentResultIndex * this.contentResultLimit;
    return loadNewContent;
  }

  private displaySaveCurrentChangesNotificationBar(): void {
    this.notificationBar.openFromComponent(NotificationBarComponent, {
      data: 'You need to save your current changes before continuing to the next page',
    });
  }

  private loadNextContentResultIndex(): void {
    this.contentResultIndex++;
    this.loadContent(this.contentResultIndex * this.contentResultLimit);
  }

  addToContentList(contents: IContent[]): void {
    this.unPinnedContent = this.sortContentArray([
      ...contents,
      ...this.unPinnedContent,
    ]);
    this.contentAdded = this.sortContentArray([
      ...contents,
      ...this.contentAdded,
    ]);

    this.removeContentsFromArray(this.contentRemoved, contents);
    if (!this.pinnedContent.length) {
      this.removeAllPinnedContent = true;
    }
    this.paginateData(this.paginator);
  }

  private sortContentArray(contents: IContent[]): IContent[] {
    // Sort by publish date descending, then by title descending -- same as displayed on site
    contents.sort((a, b) => {
      if (a.publishedDate > b.publishedDate) {
        return -1;
      } else if (a.publishedDate < b.publishedDate) {
        return 1;
      } else {
        return a.title > b.title ? -1 : 1;
      }
    });
    return contents;
  }

  pinAlreadyAddedContentToContentList(content: IContent): void {
    this.setContentPropertiesToNewlyPinned(content);
    this.pinToContentList([content]);
    this.contentListStateService.changeContentList(true);

    this.notificationBar.openFromComponent(NotificationBarComponent, {
      data:
        content.title +
        ' was pinned to content list at page ' +
        this.getPageNumber(content),
    });
  }

  pinToContentList(contents: IContent[]): void {
    contents.forEach((content) => {
      this.removeContentFromArray(this.unPinnedContent, content);
      this.removeContentFromArray(this.pinnedContent, content);
    });
    this.pinnedContent = [...this.pinnedContent, ...contents];
    this.removeContentsFromArray(this.contentRemoved, contents);
    this.paginateData(this.paginator);
    this.triggerUpdateAddContentListData.emit(contents);
  }

  pinAndAddToContentList(contents: IContent[]): void {
    const contentToBeAddedAndPinned = contents.filter(
      (content) => !content.isInContentList
    );
    const contentToBePinned = contents.filter(
      (content) => content.isInContentList
    );

    contents.forEach((content) => {
      this.setContentPropertiesToNewlyPinned(content);
    });

    if (contentToBePinned) {
      this.pinToContentList(contentToBePinned);
    }
    this.contentAdded = [...contentToBeAddedAndPinned, ...this.contentAdded];
    this.pinnedContent = [...this.pinnedContent, ...contentToBeAddedAndPinned];
    this.removeContentsFromArray(this.contentRemoved, contents);
    this.paginateData(this.paginator);
  }

  private setContentPropertiesToNewlyPinned(content: IContent): void {
    content.pinPosition = 1;
    content.isPendingPin = true;
    content.isPinned = true;
    content.isInContentList = true;
  }

  private removeContentFromArray(array: IContent[], content: IContent): void {
    const contentIndex = array.findIndex(
      (x) => x.contentId === content.contentId
    );
    if (contentIndex > -1) {
      array.splice(contentIndex, 1);
    }
  }

  private removeContentsFromArray(
    array: IContent[],
    contents: IContent[]
  ): void {
    contents.forEach((content) => {
      this.removeContentFromArray(array, content);
    });
  }

  private getPageNumber(content: IContent): number {
    const contentIndex = this.contentDataSource.filteredData.findIndex(
      (x) => x.contentId === content.contentId
    );
    return Math.floor(contentIndex / this.paginator.pageSize) + 1;
  }

  allDataLoadedAndDisplayed(listHasNextPage: boolean): boolean {
    return (
      this.totalContentCount - (this.contentResultIndex - 1) * 100 <
        this.contentResultLimit && !listHasNextPage
    );
  }

  drop(event: CdkDragDrop<IContent[]>): void {
    this.contentListStateService.changeContentList(true);
    const currentIndex =
      this.paginator.pageIndex * this.paginator.pageSize + event.currentIndex;
    const previousIndex =
      this.paginator.pageIndex * this.paginator.pageSize + event.previousIndex;
    moveItemInArray(this.pinnedContent, previousIndex, currentIndex);
    event.item.data.isPendingPin = true;
    this.pinnedContent = [...this.pinnedContent];
    this.paginateData(this.paginator);
  }

  unPinContent(contents: IContent[]): void {
    this.contentListStateService.changeContentList(true);
    if (contents.length === this.pinnedContent.length) {
      this.pinnedContent = [];
      this.removeAllPinnedContent = true;
    }
    contents.forEach((content) => {
      this.setContentPropertiesToUnpinned(content);

      if (contents.length < this.pinnedContent.length) {
        const contentIndex = this.pinnedContent.findIndex(
          (pinnedContent) => pinnedContent.contentId === content.contentId
        );
        this.pinnedContent.splice(contentIndex, 1);
        this.removeAllPinnedContent = false;
      }
    });

    this.unPinnedContent = this.sortContentArray([
      ...contents,
      ...this.unPinnedContent,
    ]);
    this.triggerUpdateAddContentListData.emit(contents);
    this.paginateData(this.paginator);
  }

  unPinAllContent(contents: IContent[]): void {
    const unPinText =
      contents.length === 1 ? contents[0].title : 'all pinned contents';
    const dialogRef = this.dialog.open(CmsDialogComponent, {
      data: {
        title: 'Unpin content',
        message: 'Are you sure you want to unpin ' + unPinText + '?',
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }

      this.unPinContent(contents);
    });
  }

  removeContent(content: IContent): void {
    const dialogRef = this.dialog.open(CmsDialogComponent, {
      data: {
        title: 'Remove content',
        message:
          'Are you sure you want to remove ' +
          content.title +
          ' from content list?',
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }

      this.contentListStateService.changeContentList(true);

      if (content.pinPosition) {
        const contentIndex = this.pinnedContent.findIndex(
          (pinnedContent) => pinnedContent.contentId === content.contentId
        );
        this.pinnedContent.splice(contentIndex, 1);
      } else {
        const contentIndex = this.unPinnedContent.findIndex(
          (unPinnedContent) => unPinnedContent.contentId === content.contentId
        );
        this.unPinnedContent.splice(contentIndex, 1);
      }

      if (!this.pinnedContent.length) {
        this.removeAllPinnedContent = true;
      }

      this.setContentPropertiesToRemoved(content);

      this.triggerUpdateAddContentListData.emit([content]);
      this.paginateData(this.paginator);

      const contentJustAdded = this.contentAdded.some(
        (contentAdded) => contentAdded.contentId === content.contentId
      );
      if (contentJustAdded) {
        this.contentAdded = this.contentAdded.filter(
          (contentAdded) => contentAdded.contentId !== content.contentId
        );
        return;
      }
      this.contentRemoved = [...this.contentRemoved, content];
    });
  }

  private setContentPropertiesToRemoved(content: IContent): void {
    content.isPinned = false;
    content.isInContentList = false;
    content.pinPosition = null;
  }

  private setContentPropertiesToUnpinned(content: IContent): void {
    content.isPinned = false;
    content.isInContentList = true;
    content.pinPosition = null;
  }

  ngOnDestroy(): void {
    this.pageSubscriptions.forEach((subscription) =>
      subscription.unsubscribe()
    );
  }
}
